import type { MutationFunctionContext } from '@tanstack/query-core';
import { type UseMutationOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import type { Item, ItemCustomFieldValue } from '@/common/api/types/items';
import { reconcileAndSettle } from '@/common/queries/itemsTreeSetSettleState';
import { getItemFetchQueryOptions } from '@/common/queries/useGetItemFetchQuery';
import { commonQueryKeys } from '@/common/queryKeys/common';
import { FieldValuesApi, type ItemFieldValue } from '@/features/items/api/fieldValues';
import type { FieldType } from '@/features/items/components/custom-field-value/types';
import {
  invalidateSingleItemQueries,
  restoreItemCache,
} from '@/features/items/utils/itemCacheUtils';
import { reconcileMultiValues } from '@/features/items/utils/itemConvert';

interface MutateArgs {
  fieldId: number;
  fieldType: FieldType;
  value: unknown;
}

type MutationContext = { previousItem: Item | undefined };

type MultiValueEntry = { id: number } & Record<string, unknown>;

type MutationResult = { upsertResponse: ItemFieldValue | null };

interface UseCustomFieldValueMutationParams {
  itemId: number;
  options?: Omit<
    UseMutationOptions<MutationResult, Error, MutateArgs, MutationContext>,
    'mutationFn' | 'onMutate'
  >;
}

export const toCacheValue = (
  fieldType: FieldType,
  value: unknown,
): ItemCustomFieldValue | undefined => {
  if (fieldType === 'LINK') {
    if (!value) return undefined;
    const link = value as { url: string; description?: string };
    return { LINK: link.url, description: link.description };
  }

  if (fieldType === 'MULTI_LINK' && Array.isArray(value)) {
    const links = value as Array<{ url: string; description?: string }>;
    return links.map((link, i) => ({
      id: -(Date.now() + i),
      MULTI_LINK: link.url,
      description: link.description,
    })) as ItemCustomFieldValue;
  }

  if (
    (fieldType === 'MULTI_TEXT' ||
      fieldType === 'MULTI_USER' ||
      fieldType === 'MULTI_CUSTOMER' ||
      fieldType === 'MULTI_PICKLIST' ||
      fieldType === 'MULTI_ITEM') &&
    Array.isArray(value)
  ) {
    const primitives = value as Array<string | number>;
    return primitives.map((v, i) => ({
      id: -(Date.now() + i),
      [fieldType]: v,
    })) as ItemCustomFieldValue;
  }

  return { [fieldType]: value } as ItemCustomFieldValue;
};

const buildOptimisticMultiValueEntries = (
  fieldType: FieldType,
  newValues: unknown[],
  currentEntries: MultiValueEntry[],
): MultiValueEntry[] => {
  const consumedIndices = new Set<number>();

  const findOnce = (predicate: (e: MultiValueEntry) => boolean): MultiValueEntry | undefined => {
    const index = currentEntries.findIndex((e, idx) => !consumedIndices.has(idx) && predicate(e));
    if (index === -1) return undefined;
    consumedIndices.add(index);
    return currentEntries[index];
  };

  if (fieldType === 'MULTI_LINK') {
    const newLinks = newValues as Array<{ url: string; description?: string }>;
    return newLinks.map((link, i) => {
      const existing = findOnce((e) => e.MULTI_LINK === link.url);
      return existing
        ? { ...existing, description: link.description }
        : { id: -(Date.now() + i), MULTI_LINK: link.url, description: link.description };
    });
  }

  const newPrimitives = newValues as (string | number)[];
  return newPrimitives.map((v, i) => {
    const existing = findOnce((e) => e[fieldType] === v);
    return existing ?? { id: -(Date.now() + i), [fieldType]: v };
  });
};

export const useCustomFieldValueMutation = ({
  itemId,
  options,
}: UseCustomFieldValueMutationParams) => {
  const queryClient = useQueryClient();
  const preMutationEntriesRef = useRef<Record<number, MultiValueEntry[]>>({});

  return useMutation<MutationResult, Error, MutateArgs, MutationContext>({
    ...options,
    mutationFn: async ({ fieldId, value }: MutateArgs): Promise<MutationResult> => {
      if (!Array.isArray(value)) {
        await FieldValuesApi.upsert(itemId, fieldId, value);
        return { upsertResponse: null };
      }

      const previousEntries = preMutationEntriesRef.current[fieldId] ?? [];
      delete preMutationEntriesRef.current[fieldId];

      const idsToDelete = previousEntries.map((e) => e.id).filter((id) => id > 0);
      if (idsToDelete.length > 0) {
        await FieldValuesApi.delete(itemId, fieldId, idsToDelete);
      }

      if (value.length > 0) {
        const upsertResponse = await FieldValuesApi.upsertMany(itemId, fieldId, value);
        return { upsertResponse };
      }

      return { upsertResponse: null };
    },

    onMutate: async ({ fieldId, fieldType, value }) => {
      await queryClient.cancelQueries({ queryKey: commonQueryKeys.item(itemId) });
      let previousItem = queryClient.getQueryData<Item>(commonQueryKeys.item(itemId));

      if (Array.isArray(value) && !previousItem) {
        // Without current field-value IDs the delete step is skipped, causing duplicates.
        previousItem = await queryClient.fetchQuery(getItemFetchQueryOptions({ itemId }));
      }

      const currentEntries = (previousItem?.customFields?.[fieldId] ?? []) as MultiValueEntry[];

      if (Array.isArray(value)) {
        preMutationEntriesRef.current[fieldId] = currentEntries;
      }

      const optimisticValue = Array.isArray(value)
        ? buildOptimisticMultiValueEntries(fieldType, value, currentEntries)
        : toCacheValue(fieldType, value);

      queryClient.setQueryData<Item>(commonQueryKeys.item(itemId), (old) => {
        if (!old) return old;
        return {
          ...old,
          customFields: { ...(old.customFields || {}), [fieldId]: optimisticValue },
        };
      });

      return { previousItem };
    },

    onError: (err, variables, context, fnContext: MutationFunctionContext) => {
      restoreItemCache(queryClient, itemId, context?.previousItem);
      options?.onError?.(err, variables, context, fnContext);
    },

    onSuccess: (data, variables, context, fnContext: MutationFunctionContext) => {
      if (data.upsertResponse) {
        const { fieldId, fieldValue, fieldType } = data.upsertResponse;
        const reconciledValues = reconcileMultiValues(fieldType, fieldValue);
        queryClient.setQueryData<Item>(commonQueryKeys.item(itemId), (old) => {
          if (!old) return old;
          return {
            ...old,
            customFields: {
              ...(old.customFields ?? {}),
              [String(fieldId)]: reconciledValues as ItemCustomFieldValue,
            },
          };
        });
      }
      invalidateSingleItemQueries(queryClient, itemId);
      void reconcileAndSettle(queryClient, [itemId]);
      options?.onSuccess?.(data, variables, context, fnContext);
    },
  });
};
